import styled from "styled-components";
import { Link } from "react-router-dom";
import { DraftGame } from "../TestList";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";
import { faEllipsisH } from "@fortawesome/free-solid-svg-icons";
import axios, { AxiosError } from "axios";
// import { Modal } from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import instance from "src/API/MainAPI";
import AlertModal from 'src/components/common/AlertModal';

interface GameBoxProps {
  game: DraftGame;
}

const GameBoxContainer = styled.div`
  position: relative;
  width: 300px;
  height: 300px;
  border-radius: 5px;
`;

const BannerImage = styled.div<{ url: string }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 220px;
  background-image: 
    linear-gradient(180deg, rgba(255,255,255,0) 70%, rgba(255,255,255,1) 100%),
    ${({ url }) => `url(${url})`};
  background-size: cover;
  background-position: center;
  border-radius: 5px 5px 0 0;
`;

const GameOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 230px;
  background: linear-gradient(180deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.3) 30%, rgba(0,0,0,0) 100%);
  border-radius: 5px 5px 0 0;
`;

const DefaultBanner = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 70%;
  background: linear-gradient(180deg, #E7E7E7 0%, #FEFEFE 100%);
  border-radius: 5px 5px 0 0;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const DefaultText = styled.p`
  font-size: 15px;
  color: #999;
  margin: 0;
`;

const StatusBadge = styled.div<{ status: string }>`
  position: absolute;
  top: 20px;
  left: 20px;
  padding: 8px 16px;
  border-radius: 20px;
  background-color: ${({ status }) => {
    switch (status) {
      case 'SUBMITTED': return '#2282E9';
      case 'REJECTED': return '#FF1F00';
      case 'APPROVED': return '#00B031';
      default: return '#8E8E8E';
    }
  }};
  color: white;
  font-size: 14px;
`;

const GameTitle = styled.h3`
  position: absolute;
  bottom: 45px;
  left: 20px;
  color: black;
  font-size: 18px;
  font-weight: 600;
  margin: 0;
`;

const ActionLink = styled(Link)`
  position: absolute;
  bottom: 20px;
  left: 20px;
  color: #2282E9;
  // text-decoration: none;
  font-size: 14px;

  
  &:hover {
    text-decoration: underline;
  }
`;

const ContentBox = styled.div`
  border-radius: 5px;
  background-color: white;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 300px;
  height: 300px;
  min-width: 300px;
  flex-shrink: 0;
`;

const GameBox = styled(ContentBox)`
  flex-direction: column;
  padding: 20px;
  justify-content: flex-end;
  align-items: flex-start;
  position: relative;
  background-color: white;
  // height: 0vh;
  // height: 400px;
`;

const MoreButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  width: 25px;
  height: 25px;
  border-radius: 50%;
  border: none;
  background-color: #EBF5FF;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  color: #666666;
  z-index: 2;
  
  &:hover {
    background-color: #f5f5f5;
  }
`;

const DropdownMenu = styled.div<{ isVisible: boolean }>`
  position: absolute;
  top: 50px;
  right: -10px;
  background-color: #EBF5FF;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  display: ${({ isVisible }) => (isVisible ? 'block' : 'none')};
  z-index: 3;
`;

const MenuItem = styled.button`
  padding: 20px 50px;
  // width: 100px;
  background: none;
  border: none;
  text-align: left;
  font-size: 14px;
  color: #555555;
  cursor: pointer;
  width: 100%;
  display: block;
  border-radius: 8px;
  // z-index: 100;
  
  &:hover {
    background-color: #f5f5f5;
  }

  &:not(:last-child) {
    border-bottom: 1px solid #f0f0f0;
  }
`;


export default function OnWritingGameBox({ game }: GameBoxProps) {
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const queryClient = useQueryClient();
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);

  const getActionText = (status: string) => {
    switch (status) {
      case 'APPROVED': return '테스트 시작하기 >';
      case 'REJECTED': return '테스트 수정하기 >';
      case 'SUBMITTED': return '테스트 미리보기 >';
      case 'IN_PROGRESS': return '이어서 작성하기 >';
      default: return '';
    }
  };

  
  const handleDeleteGameProposal = async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    try {
      await instance.delete(`/v1/labs/game-proposals/${game.gameProposalId}`);
      
      // 성공 모달 표시
      setShowSuccessModal(true);
      
      // 목록 새로고침
      // await queryClient.invalidateQueries(['GameProposalOverview']);
      
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error('Delete game proposal error:', axiosError);
      setShowErrorModal(true);
    }
  };


  return (
    <>
      <GameBoxContainer>
        <GameBox>
        {game.bannerUrl && game.bannerUrl !== "" ? (
          <>
            <BannerImage url={game.bannerUrl} />
            <GameOverlay />
          </>
        ) : (
          <DefaultBanner>
            <DefaultText>이미지 등록 필요.</DefaultText>
          </DefaultBanner>
        )}
        <GameOverlay />
        
        {game.status !== 'IN_PROGRESS' && (
          <StatusBadge status={game.status}>
            {game.status === 'SUBMITTED' && '심사중'}
            {game.status === 'REJECTED' && '반려'}
            {game.status === 'APPROVED' && '심사완료'}
          </StatusBadge>
        )}
        
        <GameTitle>{game.title}</GameTitle>
        
        <ActionLink to={`/home/test/draft/${game.gameProposalId}`}>
          {getActionText(game.status)}
        </ActionLink>

        {game.status === 'IN_PROGRESS' && (
          <>
            <MoreButton
              data-dropdown="button"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                setIsDropdownVisible(!isDropdownVisible);
              }}
            >
              <FontAwesomeIcon icon={faEllipsisH} />
            </MoreButton>
            <DropdownMenu 
              data-dropdown="menu"
              isVisible={isDropdownVisible} 
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
              <MenuItem onClick={handleDeleteGameProposal}>삭제</MenuItem>
              {/* <MenuItem onClick={handleApprovedTestEdit}>테스트 수정하기</MenuItem> */}
            </DropdownMenu>
          </>
        )}
        </GameBox>
        
      </GameBoxContainer>
      
      {/* 성공 모달 */}
      <AlertModal
        isOpen={showSuccessModal}
        message="삭제되었습니다."
        onClose={() => {setShowSuccessModal(false);
          window.location.reload();
        }}
      />

      {/* 에러 모달 */}
      <AlertModal
        isOpen={showErrorModal}
        message="삭제 중 오류가 발생했습니다."
        onClose={() => {
          setShowErrorModal(false);
          window.location.reload();
        }}
      />
    </>
  );
}